import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import {
  useChatBansQuery,
  useDeleteChatBanMutation,
} from "@/partner/entities/chat";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat } from "@/partner/shared/lib";
import { ConfirmDialog, ErrorMessage } from "@/shared/components";
import { ChatBanTypeType } from "@/types/graphql/partner.generated";
import type { ChatBansQueryType } from "@/types/graphql/partner.generated";

type BanRow = ChatBansQueryType["chatBans"]["nodes"][number];

const banUserName = (row: BanRow): string =>
  row.user?.displayName ?? row.user?.email ?? `ID ${row.userId.slice(-6)}`;

export const ChatBansList: FC = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();
  const [confirmRow, setConfirmRow] = useState<BanRow | null>(null);

  const { data, loading, error, refetch } = useChatBansQuery({
    variables: { page, rowsPerPage },
    fetchPolicy: "cache-and-network",
  });

  const [deleteChatBan, { loading: deleting }] = useDeleteChatBanMutation({
    onCompleted: () => {
      enqueueSnackbar(t("chat.bans.deletedSnackbar"), { variant: "success" });
      void refetch();
    },
    onError: (mutationError) =>
      enqueueSnackbar(mutationError.message, { variant: "error" }),
  });

  const rows = data?.chatBans?.nodes ?? [];
  const count = data?.chatBans?.count ?? 0;

  const handleConfirm = async () => {
    if (confirmRow) await deleteChatBan({ variables: { id: confirmRow.id } });
    setConfirmRow(null);
  };

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) return <EmptyListCard title={t("chat.bans.empty")} />;

  return (
    <Card elevation={0} sx={{ overflowX: "auto" }}>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell>{t("chat.bans.userColumn")}</TableCell>
            <TableCell sx={{ width: 160 }}>
              {t("chat.bans.typeColumn")}
            </TableCell>
            <TableCell sx={{ width: 180 }}>
              {t("chat.bans.createdAtColumn")}
            </TableCell>
            <TableCell sx={{ width: 180 }}>
              {t("chat.bans.expiredAtColumn")}
            </TableCell>
            <TableCell align="right" sx={{ width: 140 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>{banUserName(row)}</TableCell>
              <TableCell>{t(`chat.bans.type.${row.type}`)}</TableCell>
              <TableCell>
                {interpolationFormat(row.createdAt, "dateTime", i18n.language)}
              </TableCell>
              <TableCell>
                {/* У постоянного бана expiredAt стоит на сто лет вперёд:
                    показывать такую дату бессмысленно. */}
                {row.type === ChatBanTypeType.PermanentType
                  ? t("chat.bans.noExpiry")
                  : interpolationFormat(
                      row.expiredAt,
                      "dateTime",
                      i18n.language
                    )}
              </TableCell>
              <TableCell align="right">
                <Button size="small" onClick={() => setConfirmRow(row)}>
                  {t("chat.bans.deleteAction")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={Boolean(confirmRow)}
        loading={deleting}
        confirmTitle={t("chat.bans.deleteAction")}
        confirmText={confirmRow ? banUserName(confirmRow) : ""}
        onCancel={() => setConfirmRow(null)}
        onConfirm={() => void handleConfirm()}
      />

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count: total }) =>
          t("common.pagination.displayedRows", { from, to, count: total })
        }
      />
    </Card>
  );
};
